import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';
import './HistoricChart.css';

const CHART_HEIGHT = 350;
/** Roughly the width of one formatted axis label plus breathing room. */
const PX_PER_X_LABEL = 90;
const MAX_X_TICKS = 8;
const MIN_X_TICKS = 2;

interface HistoricChartProps {
  data: any[];
  selectedMetric: string;
  metricLabel: string;
  metricUnit: string;
  timeframe: string;
  domain: [number, number];
}

export function HistoricChart({ data, selectedMetric, metricLabel, metricUnit, timeframe, domain }: HistoricChartProps) {
  // Measured so the axis can drop labels it has no room for.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(entries => {
      const measured = entries[0]?.contentRect.width;
      if (typeof measured === 'number') setWidth(measured);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Format dates nicely on the X axis
  const formatXAxis = (ms: number) => {
    try {
      const date = new Date(ms);
      if (timeframe === '24h') {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
      } else {
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }
    } catch {
      return '';
    }
  };

  // Recharts derives ticks from the plotted values when none are supplied, so a
  // range with one cluster of readings renders every label bunched at that
  // cluster and leaves the rest of the axis unlabelled. Space them across the
  // selected range instead, so the axis always describes the window on screen.
  // Depend on the bounds themselves: the caller passes a fresh array literal
  // each render, so memoizing on `domain` would recompute every time.
  const [domainStart, domainEnd] = domain;
  const xTicks = useMemo(() => {
    const start = domainStart;
    const end = domainEnd;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return undefined;
    // Counts chosen so each step lands on a round unit for the range in view
    // (8 over 7 days = one tick per day, 7 over 24h = one per 4 hours), which
    // keeps consecutive labels from collapsing to the same formatted date...
    const preferred = timeframe === '7d' ? MAX_X_TICKS : 7;
    // ...then thinned to what actually fits, so a phone-width chart doesn't
    // render its date labels on top of each other.
    const fits = width > 0 ? Math.floor(width / PX_PER_X_LABEL) : preferred;
    const tickCount = Math.max(MIN_X_TICKS, Math.min(preferred, fits));
    const step = (end - start) / (tickCount - 1);
    return Array.from({ length: tickCount }, (_, i) => Math.round(start + step * i));
  }, [domainStart, domainEnd, timeframe, width]);

  if (!data || data.length === 0) {
    return (
      <div className="historic-chart-empty" style={{ height: CHART_HEIGHT }}>
        <p className="historic-chart-empty-title">No readings in this period</p>
        <p className="historic-chart-empty-hint">
          Pick a different range, or step back with the arrows to find earlier data.
        </p>
      </div>
    );
  }

  // Custom tooltip content to match our global styles
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dateLabel = new Date(label).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      return (
        <div style={{
          backgroundColor: 'var(--color-bg-elevated)',
          border: '1px solid var(--color-border-primary)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-3)',
          boxShadow: 'var(--shadow-md)',
          fontFamily: 'var(--font-family)',
          fontSize: 'var(--font-size-sm)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold', color: 'var(--color-text-primary)' }}>{dateLabel}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ margin: '4px 0 0 0', color: entry.color, display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
              <span>{entry.name}:</span>
              <strong>
                {entry.value !== undefined && entry.value !== null 
                  ? `${Number(entry.value).toFixed(entry.name.includes('Rate') ? 3 : 1)} ${metricUnit}` 
                  : 'N/A'}
              </strong>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div ref={wrapperRef} style={{ width: '100%', height: CHART_HEIGHT, minWidth: 0 }}>
      {/* initialDimension defaults to {-1,-1}, so the first paint renders a
          negative-sized chart and Recharts logs a size warning before its
          ResizeObserver reports the real box. Seed it with a plausible size. */}
      <ResponsiveContainer
        width="100%"
        height="100%"
        minWidth={0}
        initialDimension={{ width: 600, height: CHART_HEIGHT }}
      >
        <LineChart
          data={data}
          // Right gutter holds the final X tick label, which sits on the domain
          // edge and would otherwise be clipped by the container. The left inset
          // stays at 0 because a negative one clips wide Y labels — a full tank
          // runs to five digits of litres.
          margin={{ top: 10, right: 28, left: 0, bottom: 5 }}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="var(--color-border-secondary)" 
          />
          <XAxis
            dataKey="timestampMs"
            type="number"
            scale="time"
            domain={domain}
            ticks={xTicks}
            interval={0}
            allowDataOverflow
            tickFormatter={formatXAxis}
            stroke="var(--color-text-tertiary)"
            style={{ fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family)' }}
            dy={8}
          />

          <YAxis
            domain={['auto', 'auto']}
            width={64}
            tickFormatter={(v: number) => (Math.abs(v) >= 1000 ? v.toLocaleString() : String(v))}
            stroke="var(--color-brand-primary)"
            style={{ fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family)' }}
            dx={-4}
          />

          <Tooltip content={<CustomTooltip />} />


          <Line
            type="monotone"
            dataKey={selectedMetric}
            name={metricLabel}
            stroke="var(--color-brand-primary)"
            strokeWidth={3}
            dot={false}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
